import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function LogoUpload({ agentId, currentLogo, onLogoUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentLogo);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/agents/${agentId}/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Logo uploaded successfully!');
      if (onLogoUpdated) {
        onLogoUpdated(preview);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
      setPreview(currentLogo); // Revert preview on error
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.put(`/agents/${agentId}`, { logo: null });
      setPreview(null);
      toast.success('Logo removed');
      if (onLogoUpdated) {
        onLogoUpdated(null);
      }
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Agent Logo</Label>
      
      <div className="flex items-start space-x-4">
        {/* Preview */}
        <div className="relative">
          {preview ? (
            <div className="relative w-24 h-24 rounded-xl border-2 border-border overflow-hidden bg-white">
              <img
                src={preview}
                alt="Agent logo"
                className="w-full h-full object-contain"
              />
              <button
                onClick={handleRemoveLogo}
                className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                data-testid="remove-logo"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-muted/30">
              <ImageIcon className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Upload Button */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="logo-file-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="rounded-full"
            data-testid="upload-logo-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : preview ? 'Change Logo' : 'Upload Logo'}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Recommended: Square image, max 2MB (JPEG, PNG, GIF, or WebP)
          </p>
        </div>
      </div>
    </div>
  );
}
